import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { AlertTriangle, Bot, Check, FilePenLine, Mic, Phone, SkipForward } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { uploadFile } from '../lib/upload';
import { useI18n } from '../lib/i18n';
import { FileDrop } from './FileDrop';
import { Module } from './ui/Module';
import { RChip } from './ui/RChip';
import { AiBadge } from './ui/AiBadge';
import { DecisionBar } from './ui/DecisionBar';

type Attempt = { id: string; source: string; recording_url: string | null; note: string | null; call_result: string | null; analysis: Record<string, unknown> | null; created_at: string };
type WC = { source: string; recording_url: string | null; note: string | null; done_at: string | null; status?: string | null; call_result?: string | null; problem?: boolean; problem_reason?: string | null; analysis?: Record<string, unknown> | null; attempts?: Attempt[] } | null;

const RESULTS = ['accepted', 'not_accepted', 'done', 'no_answer', 'needs_human'] as const;

/** Caja de respuestas del homeowner (las que van al intake). */
const ANSWER_FIELDS: [string, string][] = [
  ['isPolicyholder', 'Policyholder'], ['nameConfirmed', 'Name confirmed'], ['emailConfirmed', 'Email confirmed'],
  ['emailCorrected', 'Corrected email'], ['contractSigned', 'Contract signed'], ['contractLinkNeeded', 'Needs signing link'],
  ['authorized', 'Authorized'], ['callStatus', 'Call status'], ['callbackTime', 'Callback time'],
  ['roofAge', 'Roof age'], ['leaks', 'Leaks'], ['roofRepairs', 'Prior roof repairs'], ['previousClaims', 'Previous claims'],
  ['gateCode', 'Gate code'], ['preferredLanguage', 'Preferred language'], ['additionalNotes', 'Notes'],
];
function WelcomeCallAnswers({ analysis, note }: { analysis: Record<string, unknown> | null; note: string | null }) {
  const rows = ANSWER_FIELDS.map(([k, label]) => [label, analysis?.[k]] as const).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!rows.length) return <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{note || '—'}</div>;
  const fmt = (v: unknown) => (v === true ? 'Yes' : v === false ? 'No' : String(v));
  return (
    // display block (no grid): así las filas FLUYEN cuando el padre usa CSS columns (2-3 cols).
    <div>
      {/* La nota es el resumen de la llamada ("completed · inspección autorizada…"): arriba de las
          respuestas, que antes la escondían en cuanto había una sola. */}
      {note && <div className="muted" style={{ fontSize: 'var(--fs-xs)', padding: '0 0 4px', breakInside: 'avoid', overflowWrap: 'anywhere' }}>{note}</div>}
      {rows.map(([label, v]) => (
        <div key={label} style={{ display: 'flex', gap: 8, fontSize: 'var(--fs-xs)', borderBottom: '1px solid var(--line)', padding: '2px 0 4px', breakInside: 'avoid' }}>
          {/* El label ya no fuerza 110px: en una tarjeta angosta empujaba el valor fuera del
              cuadro (las notas largas se salían encima de la tarjeta de al lado). */}
          <span className="muted" style={{ flex: '0 1 auto', minWidth: 0 }}>{label}</span>
          <span style={{ fontWeight: 500, minWidth: 0, overflowWrap: 'anywhere', marginLeft: 'auto', textAlign: 'right' }}>{fmt(v)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Área de Welcome call en el Intake del claim: llamada IA o subir grabación + respuestas.
 *
 * `readOnly` deja SOLO lo que se mira (grabación, intentos, respuestas) y esconde todo lo que
 * escribe. Se usa donde la llamada ya pasó y uno solo quiere escucharla —"Ready to be filed"—:
 * ahí el botón "Call with AI" llamaría de verdad al homeowner y, peor, el re-disparo borra la
 * grabación y el análisis del claim (ON CONFLICT en claims/bland.ts).
 */
export function WelcomeCallBox({ claimId, onChanged, readOnly, homeownerName }: { claimId: string; onChanged?: () => void; readOnly?: boolean; homeownerName?: string | null }) {
  const { t } = useI18n();
  const [wc, setWc] = useState<WC>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [selId, setSelId] = useState<string | null>(null);

  async function load() { try { setWc((await api.get<{ welcome_call: WC }>(`/api/claims/${claimId}/welcome-call`)).welcome_call); } catch { /* ignore */ } }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);

  async function aiCall() {
    setBusy(true); setMsg(null);
    try { await api.post(`/api/claims/${claimId}/welcome-call/ai`, {}); setMsg(t('wc.aiRequested')); await load(); onChanged?.(); }
    catch (e) {
      const raw = e instanceof ApiError ? e.message : 'Error';
      const map: Record<string, string> = {
        no_phone: t('wc.errNoPhone'), bland_not_configured: t('wc.errNotConfigured'),
        bland_unreachable: t('wc.errUnreachable'),
      };
      setMsg(map[raw] || raw);
    } finally { setBusy(false); }
  }
  async function upload(file: File) {
    // CONFIRMACIÓN CON EL NOMBRE (caso Louis Goss 2026-08-14: una grabación de OTRO cliente
    // quedó guardada acá a mano — con varias filas abiertas en la cola es fácil soltar el mp3
    // en la equivocada). Un vistazo al nombre antes de guardar corta el error.
    let nombre = homeownerName ?? null;
    if (!nombre) {
      try { nombre = (await api.get<{ homeowner_name?: string | null }>(`/api/claims/${claimId}`)).homeowner_name ?? null; } catch { /* sigue sin nombre */ }
    }
    if (!window.confirm(t('wc.uploadConfirm', { n: nombre || `#${claimId}`, f: file.name }))) return;
    setBusy(true); setMsg(null);
    try { const key = await uploadFile(file, 'misc'); await api.post(`/api/claims/${claimId}/welcome-call/recording`, { fileKey: key }); setMsg(t('wc.uploaded')); await load(); onChanged?.(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function setResult(result: string) {
    setBusy(true); setMsg(null);
    try { await api.post(`/api/claims/${claimId}/welcome-call/result`, { result }); await load(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function toggleProblem() {
    const next = !wc?.problem;
    const reason = next ? (window.prompt(t('wc.problemReason')) ?? '') : '';
    setBusy(true); setMsg(null);
    try { await api.post(`/api/claims/${claimId}/welcome-call/problem`, { problem: next, reason }); await load(); onChanged?.(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function markDone() {
    if (!window.confirm(t('wc.doneConfirm'))) return;
    setBusy(true); setMsg(null);
    try { await api.post(`/api/claims/${claimId}/welcome-call/done`, {}); setMsg(t('wc.movedReadyFile')); await load(); onChanged?.(); }
    catch (e) {
      const raw = e instanceof ApiError ? e.message : 'Error';
      setMsg(raw === 'welcome_call_needs_recording' ? t('wc.needRecording') : raw);
    } finally { setBusy(false); }
  }
  /** Saca al claim de Welcome Call SIN grabación (pedido 2026-07-29: hay claims que no la
   *  necesitan — ya se habló por otra vía — y quedaban atascados porque markDone la exige). */
  async function bypass() {
    const reason = window.prompt(t('wc.bypassPrompt'));
    if (reason === null) return; // canceló
    setBusy(true); setMsg(null);
    try { await api.post(`/api/claims/${claimId}/welcome-call/bypass`, { reason }); setMsg(t('wc.bypassed')); await load(); onChanged?.(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  // Chip del header del módulo: estado + botón "Call with AI" (stopPropagation: el header colapsa).
  const headChip = (
    <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
      {wc?.done_at && <RChip variant="ok" icon={Check}>{t('wc.done')}</RChip>}
      {wc?.status === 'calling' && !wc?.done_at && <RChip variant="warn" icon={Phone}>{t('wc.calling')}</RChip>}
      {wc?.source === 'ai' && !wc?.done_at && wc?.status !== 'calling' && <RChip variant="info" icon={Bot}>{t('wc.aiPending')}</RChip>}
      {/* La llamada y el análisis de respuestas los hace la IA (Bland) → insignia estándar. */}
      <AiBadge title={t('wc.aiHint')} />
      {!readOnly && <button className="btn sm" disabled={busy} onClick={aiCall} title={t('wc.aiHint')} style={{ fontFamily: 'var(--font-sans)' }}><Bot size={14} strokeWidth={1.75} /> {t('wc.callAi')}</button>}
    </span>
  );
  return (
    <Module id={`welcome-call:${claimId}`} icon={Phone} title={t('wc.title')} chip={headChip}
      preview={wc?.done_at ? t('wc.done') : wc?.status === 'calling' ? t('wc.calling') : t('iq.wcPending')}
      defaultOpen>
      <div style={{ paddingTop: 2 }}>
      {msg && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6 }}>{msg}</div>}

      {/* Llamadas hechas: GRABACIÓN a todo el ancho arriba + respuestas ABAJO en 2-3 columnas. */}
      {(() => {
        let attempts = wc?.attempts ?? [];
        // Fallback: si no hay attempts listados pero la llamada principal trae análisis o
        // grabación, se muestra esa (las llamadas sin recording no entran a attempts).
        if (!attempts.length && wc && (wc.analysis || wc.recording_url)) {
          attempts = [{ id: 'main', source: wc.source, recording_url: wc.recording_url, note: wc.note, call_result: wc.call_result ?? null, analysis: wc.analysis ?? null, created_at: wc.done_at ?? new Date().toISOString() }];
        }
        if (!attempts.length) return null;
        const sel = attempts.find((a) => a.id === selId) ?? attempts[0];
        return (
          <div style={{ marginBottom: 10, display: 'grid', gap: 8 }}>
            {attempts.length > 1 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {attempts.map((a) => (
                  <button key={a.id} className={`btn ghost sm${sel.id === a.id ? ' active' : ''}`} style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setSelId(a.id)}>
                    {a.source === 'ai' ? <Bot size={12} strokeWidth={1.75} /> : <Mic size={12} strokeWidth={1.75} />} {new Date(a.created_at).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {a.call_result ? ` · ${t(`wc.res.${a.call_result}`)}` : ''}
                  </button>
                ))}
              </div>
            )}
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}><Mic size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('wc.recording')}</div>
              {sel.recording_url ? <audio controls src={sel.recording_url} style={{ width: '100%' }} /> : <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>—</div>}
            </div>
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}><FilePenLine size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('wc.answers')}</div>
              {/* SIEMPRE 2 columnas (el módulo no se estira hacia abajo y queda a la altura del contrato). */}
              {/* Dos columnas SOLO si caben (columns: ancho mínimo + máximo de columnas): en una
                  tarjeta angosta baja sola a una y deja de amontonarse. */}
              <div style={{ columns: '150px 2', columnGap: 16 }}>
                <WelcomeCallAnswers analysis={sel.analysis} note={sel.note} />
              </div>
            </div>
          </div>
        );
      })()}

      {!readOnly && <div style={{ display: 'grid', gap: 10 }}>
        <FileDrop onFile={upload} accept="audio/*" label={`⬆ ${t('wc.uploadRecording')}`} busy={busy} busyLabel={t('cd.uploading')} compact />

        {/* Resultado de la llamada + Giving Problems. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: 8 }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('wc.result')}:</span>
          <select className="input sm" style={{ width: 170 }} value={wc?.call_result ?? ''} disabled={busy} onChange={(e) => setResult(e.target.value)}>
            <option value="">—</option>
            {RESULTS.map((r) => <option key={r} value={r}>{t(`wc.res.${r}`)}</option>)}
          </select>
          <button className={`btn ghost sm${wc?.problem ? ' active' : ''}`} disabled={busy} style={wc?.problem ? { color: 'var(--red)' } : {}} onClick={toggleProblem}>
            {wc?.problem ? <><AlertTriangle size={14} strokeWidth={1.75} /> {t('wc.problemOn')}</> : t('wc.flagProblem')}
          </button>
        </div>
        {wc?.problem && wc?.problem_reason && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><AlertTriangle size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {wc.problem_reason}</div>}

        {/* Marcar como Done → Ready to File MUEVE el claim de etapa → DecisionBar + sólido verde
            (principio del rediseño: el color fuerte queda reservado a las decisiones). */}
        <DecisionBar>
          {!wc?.done_at
            ? <button className="btn sm solid-good" disabled={busy} onClick={markDone}><Check size={14} strokeWidth={1.75} /> {t('wc.markDone')}</button>
            : <button className="btn ghost sm" disabled={busy} onClick={markDone}>→ {t('wc.toReadyFile')}</button>}
          {!wc?.done_at && <button className="btn ghost sm" disabled={busy} title={t('wc.bypassHint')} onClick={bypass}><SkipForward size={14} strokeWidth={1.75} /> {t('wc.bypass')}</button>}
        </DecisionBar>
      </div>}
      </div>
    </Module>
  );
}
